import type { RouteObject } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import Home from "@/pages/home/page";
import PublicLayout from "@/components/feature/PublicLayout";
import OwnerLayout from "@/components/feature/OwnerLayout";
import AdminLayout from "@/components/feature/AdminLayout";

import Directory from "@/pages/directory/page";
import Categories from "@/pages/categories/page";
import CategoryAtoZ from "@/pages/categories/az/page";
import CategoryDetail from "@/pages/category/page";
import Locations from "@/pages/locations/page";
import LocationDetail from "@/pages/location/page";
import Featured from "@/pages/featured/page";
import BusinessProfile from "@/pages/business/page";
import TouristSpots from "@/pages/tourist-spots/page";
import TouristSpotDetail from "@/pages/tourist-spots/detail/page";
import ListingPlans from "@/pages/listing-plans/page";
import ListYourBusiness from "@/pages/list-your-business/page";
import About from "@/pages/about/page";
import WhyUs from "@/pages/why-us/page";
import Faqs from "@/pages/faqs/page";
import Contact from "@/pages/contact/page";
import Terms from "@/pages/terms/page";
import PrivacyPolicy from "@/pages/legal/privacy-policy/page";
import TermsOfService from "@/pages/legal/terms-of-service/page";
import AdDisclaimer from "@/pages/legal/ad-disclaimer/page";
import RefundPolicy from "@/pages/legal/refund-policy/page";
import EmergencyServices from "@/pages/emergency-services/page";

import Login from "@/pages/login/page";
import Register from "@/pages/register/page";
import ForgotPassword from "@/pages/forgot-password/page";
import ResetPassword from "@/pages/reset-password/page";
import Dashboard from "@/pages/dashboard/page";
import MyListings from "@/pages/dashboard/listings/page";
import ListingEditor from "@/pages/dashboard/listings/editor/page";
import OwnerOrders from "@/pages/dashboard/orders/page";
import OwnerReviews from "@/pages/dashboard/reviews/page";
import OwnerLeads from "@/pages/dashboard/leads/page";
import OwnerMediaLibrary from "@/pages/dashboard/media/page";
import OwnerProfilePage from "@/pages/dashboard/profile/page";

import AdminDashboard from "@/pages/admin/page";
import AdminAnalytics from "@/pages/admin/analytics/page";
import AdminBusinesses from "@/pages/admin/businesses/page";
import AdminBusinessEditor from "@/pages/admin/businesses/editor/page";
import AdminApprovals from "@/pages/admin/approvals/page";
import AdminFeatured from "@/pages/admin/featured/page";
import AdminCategories from "@/pages/admin/categories/page";
import AdminSubcategories from "@/pages/admin/subcategories/page";
import AdminLocations from "@/pages/admin/locations/page";
import AdminTouristSpots from "@/pages/admin/tourist-spots/page";
import AdminMedia from "@/pages/admin/media/page";
import AdminUsers from "@/pages/admin/users/page";
import AdminReviews from "@/pages/admin/reviews/page";
import AdminLeads from "@/pages/admin/leads/page";
import AdminAdvertisements from "@/pages/admin/advertisements/page";
import AdminAdPlacements from "@/pages/admin/ad-placements/page";
import AdminPopupAds from "@/pages/admin/popup-ads/page";
import AdminAnnouncements from "@/pages/admin/announcements/page";
import AdminSubscriptions from "@/pages/admin/subscriptions/page";
import AdminPlans from "@/pages/admin/plans/page";
import AdminSeo from "@/pages/admin/seo/page";
import AdminSettings from "@/pages/admin/settings/page";
import AdminSecurity from "@/pages/admin/security/page";
import AdminActivityLog from "@/pages/admin/activity/page";
import AdminCrmContacts from "@/pages/admin/crm/contacts/page";
import AdminCrmOrders from "@/pages/admin/crm/orders/page";
import AdminCrmInvoices from "@/pages/admin/crm/invoices/page";
import AdminInvoicePrint from "@/pages/admin/crm/invoices/print/page";

const publicPages: RouteObject[] = [
  { path: "directory", element: <Directory /> },
  { path: "categories", element: <Categories /> },
  { path: "categories-a-z", element: <CategoryAtoZ /> },
  { path: "category/:slug", element: <CategoryDetail /> },
  { path: "locations", element: <Locations /> },
  { path: "location/:slug", element: <LocationDetail /> },
  { path: "featured-listings", element: <Featured /> },
  { path: "business/:slug", element: <BusinessProfile /> },
  { path: "tourist-spots", element: <TouristSpots /> },
  { path: "spot/:slug", element: <TouristSpotDetail /> },
  { path: "listing-plans", element: <ListingPlans /> },
  { path: "list-your-business", element: <ListYourBusiness /> },
  { path: "about", element: <About /> },
  { path: "why-us", element: <WhyUs /> },
  { path: "faqs", element: <Faqs /> },
  { path: "contact", element: <Contact /> },
  { path: "terms", element: <Terms /> },
  { path: "privacy-policy", element: <PrivacyPolicy /> },
  { path: "terms-of-service", element: <TermsOfService /> },
  { path: "ad-disclaimer", element: <AdDisclaimer /> },
  { path: "refund-policy", element: <RefundPolicy /> },
  { path: "emergency-services", element: <EmergencyServices /> },
];

const adminRoutes: RouteObject[] = [
  { index: true, element: <AdminDashboard /> },
  { path: "analytics", element: <AdminAnalytics /> },
  { path: "businesses", element: <AdminBusinesses /> },
  { path: "businesses/:id", element: <AdminBusinessEditor /> },
  { path: "approvals", element: <AdminApprovals /> },
  { path: "featured", element: <AdminFeatured /> },
  { path: "categories", element: <AdminCategories /> },
  { path: "subcategories", element: <AdminSubcategories /> },
  { path: "locations", element: <AdminLocations /> },
  { path: "tourist-spots", element: <AdminTouristSpots /> },
  { path: "media", element: <AdminMedia /> },
  { path: "users", element: <AdminUsers /> },
  { path: "reviews", element: <AdminReviews /> },
  { path: "leads", element: <AdminLeads /> },
  { path: "advertisements", element: <AdminAdvertisements /> },
  { path: "popup-ads", element: <AdminPopupAds /> },
  { path: "ad-placements", element: <AdminAdPlacements /> },
  { path: "subscriptions", element: <AdminSubscriptions /> },
  { path: "plans", element: <AdminPlans /> },
  { path: "seo", element: <AdminSeo /> },
  { path: "security", element: <AdminSecurity /> },
  { path: "activity", element: <AdminActivityLog /> },
  { path: "settings", element: <AdminSettings /> },
  { path: "announcements", element: <AdminAnnouncements /> },
  { path: "crm/contacts", element: <AdminCrmContacts /> },
  { path: "crm/orders", element: <AdminCrmOrders /> },
  { path: "crm/invoices", element: <AdminCrmInvoices /> },
  { path: "crm/invoices/:id/print", element: <AdminInvoicePrint /> },
];

const routes: RouteObject[] = [
  {
    path: "/",
    element: <PublicLayout />,
    children: [{ index: true, element: <Home /> }, ...publicPages, { path: "*", element: <NotFound /> }],
  },
  { path: "/login", element: <Login /> },
  { path: "/register", element: <Register /> },
  { path: "/forgot-password", element: <ForgotPassword /> },
  { path: "/reset-password", element: <ResetPassword /> },
  {
    path: "/dashboard",
    element: <OwnerLayout />,
    children: [
      { index: true, element: <Dashboard /> },
      { path: "listings", element: <MyListings /> },
      { path: "listings/new", element: <ListingEditor /> },
      { path: "listings/:id", element: <ListingEditor /> },
      { path: "orders", element: <OwnerOrders /> },
      { path: "reviews", element: <OwnerReviews /> },
      { path: "leads", element: <OwnerLeads /> },
      { path: "media", element: <OwnerMediaLibrary /> },
      { path: "profile", element: <OwnerProfilePage /> },
    ],
  },
  {
    path: "/admin",
    element: <AdminLayout />,
    children: adminRoutes,
  },
];

export default routes;